import { enUS } from 'date-fns/locale/en-US';
import { enIN } from 'date-fns/locale/en-IN';
import { enGB } from 'date-fns/locale/en-GB';
import { enAU } from 'date-fns/locale/en-AU';
import { enCA } from 'date-fns/locale/en-CA';
import { enNZ } from 'date-fns/locale/en-NZ';
import { enZA } from 'date-fns/locale/en-ZA';
import { de } from 'date-fns/locale/de';
import { fr } from 'date-fns/locale/fr';

// Maps CF-IPCountry code to BCP 47 locale string.
// Falls back to navigator.language, then 'en-US'.
const COUNTRY_TO_LOCALE = {
  IN: 'en-IN',
  US: 'en-US',
  GB: 'en-GB',
  CA: 'en-CA',
  AU: 'en-AU',
  NZ: 'en-NZ',
  ZA: 'en-ZA',
  SG: 'en-SG',
  AE: 'en-AE',
  DE: 'de-DE',
  FR: 'fr-FR',
};

let _locale = null;
let _country = null; // raw country code (e.g., 'IN', 'US')
let _listeners = [];
// Defaults, overwritten by the /locale API — keep dailyVerified in sync with
// sitaare-api/config/freeCredits.json: it's baked into prerendered copy (HelpPage FAQ).
let _freeCredits = { signupImmediate: 1, tempCreation: 1, dailyVerified: 3 };

export const setLocaleFromCountry = (country) => {
  _country = country || null;
  _locale = (country && COUNTRY_TO_LOCALE[country]) || navigator.language || 'en-US';
};

export const setCountryOverride = (country) => {
  setLocaleFromCountry(country);
  localStorage.setItem('countryOverride', country);
  _listeners.forEach(fn => fn(country));
};

export const getCountry = () => _country;

export const setFreeCredits = (freeCredits) => {
  if (freeCredits) _freeCredits = freeCredits;
};

export const getFreeCredits = () => _freeCredits;

export const onCountryChange = (fn) => {
  _listeners.push(fn);
  return () => { _listeners = _listeners.filter(l => l !== fn); };
};

export const getLocale = () => _locale || navigator.language || 'en-US';

// date-fns locale object for MUI DatePicker/TimePicker
const LOCALE_TO_DATEFNS = {
  'en-US': enUS, 'en-IN': enIN, 'en-GB': enGB, 'en-AU': enAU,
  'en-CA': enCA, 'en-NZ': enNZ, 'en-ZA': enZA,
  'de-DE': de, 'fr-FR': fr,
};

export const getDateFnsLocale = () => LOCALE_TO_DATEFNS[getLocale()] || enUS;

// "Mar 25, 2026" or "25 Mar 2026" depending on locale
export const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Intl.DateTimeFormat(getLocale(), { year: 'numeric', month: 'short', day: 'numeric' }).format(new Date(dateStr));
};

// "Mar 25, 2026, 3:45 PM" or locale equivalent
export const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  return new Intl.DateTimeFormat(getLocale(), {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  }).format(new Date(dateStr));
};

// "Mar 25" or "25 Mar" depending on locale
export const formatShortDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Intl.DateTimeFormat(getLocale(), { month: 'short', day: 'numeric' }).format(new Date(dateStr));
};

// "$3.99" or "₹299" depending on currency
export const formatCurrency = (amount, currency) => {
  return new Intl.NumberFormat(getLocale(), {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: currency === 'inr' ? 0 : 2,
    maximumFractionDigits: currency === 'inr' ? 0 : 2,
  }).format(amount);
};
